import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = ["src"]

import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import VimeoPlayer from '@vimeo/player'
import { bindVimeoPlayerSignal } from '../../learning/videoSignal'
import type { Milestone, VideoSignalHandlers } from '../../learning/videoSignal'
import { VideoEngagementReporter } from '../../learning/videoEngagement'
import {
  createYouTubePlayer,
  YouTubePlayerSignal
} from '../../learning/youtubePlayer'
import type { YouTubePlayerLike } from '../../learning/youtubePlayer'


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoWrapper',
  props: {
    player: {},
    videoId: {},
    courseId: { default: 0 },
    lessonId: { default: 0 },
    analyticsEnabled: { type: Boolean, default: false },
    options: { default: () => ({}) }
  },
  emits: ["play", "pause", "milestone"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// Play/pause/milestone signals for the learning-time meter (spec §2.2/§7 task 2-3a).
const emit = __emit

const youtubeIframe = ref<HTMLIFrameElement | null>(null)
const vimeoIframe = ref<HTMLIFrameElement | null>(null)
let youtubePlayer: YouTubePlayerLike | null = null
let vimeoPlayer: VimeoPlayer | null = null
let detachSignal: (() => void) | null = null
let engagementReporter: VideoEngagementReporter | null = null
let unmounted = false
const onPageHide = () => engagementReporter?.pagehide()

const startEngagementReporter = () => {
  if (engagementReporter || !props.analyticsEnabled || props.courseId <= 0 || props.lessonId <= 0) return

  engagementReporter = new VideoEngagementReporter({ courseId: props.courseId, lessonId: props.lessonId })
  window.addEventListener('pagehide', onPageHide)
}

watch(
  () => [props.analyticsEnabled, props.courseId, props.lessonId],
  startEngagementReporter
)

const signalHandlers: VideoSignalHandlers = {
  onPlay: () => {
    engagementReporter?.play()
    emit('play')
  },
  onPause: (data) => {
    engagementReporter?.pause(data)
    emit('pause')
  },
  onMilestone: (milestone) => emit('milestone', milestone),
  onTimeUpdate: (data) => engagementReporter?.position(data),
  onSeeked: (data) => engagementReporter?.seeked(data),
  onEnded: (data) => {
    engagementReporter?.ended(data)
    emit('pause')
  }
}

onMounted(async () => {
  startEngagementReporter()

  if (props.player === 'vimeo' && vimeoIframe.value) {
    vimeoPlayer = new VimeoPlayer(vimeoIframe.value)
    detachSignal = bindVimeoPlayerSignal(vimeoPlayer, signalHandlers)
  }

  if (props.player === 'youtube' && youtubeIframe.value) {
    try {
      let signal: YouTubePlayerSignal | null = null
      const player = await createYouTubePlayer(
        youtubeIframe.value,
        (event) => signal?.stateChanged(event.data)
      )
      if (unmounted) {
        player.destroy()
        return
      }
      youtubePlayer = player
      signal = new YouTubePlayerSignal(player, signalHandlers)
      detachSignal = () => signal?.dispose()
    } catch {
      // Playback still works when the optional analytics API cannot load.
    }
  }
})

onBeforeUnmount(() => {
  unmounted = true
  detachSignal?.()
  detachSignal = null
  window.removeEventListener('pagehide', onPageHide)
  engagementReporter?.dispose()
  engagementReporter = null
  youtubePlayer?.destroy()
  youtubePlayer = null
  vimeoPlayer?.destroy()
  vimeoPlayer = null
})

const youtubeUrl = computed((): string => {
  // Support both full YouTube URLs and video IDs
  let id = props.videoId
  if (props.videoId.includes('youtube.com') || props.videoId.includes('youtu.be')) {
    // Extract video ID from URL
    const match = props.videoId.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i)
    if (match) {
      id = match[1]
    }
  }
  return `https://www.youtube.com/embed/${id}?rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
})

const vimeoUrl = computed((): string => {
  // Support both full Vimeo URLs and video IDs
  let id = props.videoId
  if (props.videoId.includes('vimeo.com')) {
    const match = props.videoId.match(/vimeo\.com\/(?:.*\/)?(\\d+)/i)
    if (match) {
      id = match[1]
    }
  }
  return `https://player.vimeo.com/video/${id}`
})

const containerStyle = computed((): Record<string, string> => {
  if (props.options?.responsive) {
    return {
      position: 'relative',
      paddingBottom: '56.25%', // 16:9 aspect ratio
      height: '0',
      overflow: 'hidden'
    }
  }
  return {
    width: props.options?.width || '100%',
    height: props.options?.height || '100%'
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "video-wrapper",
    style: _normalizeStyle(containerStyle.value)
  }, [
    _createCommentVNode(" YouTube "),
    (__props.player === 'youtube' && __props.videoId)
      ? (_openBlock(), _createElementBlock("iframe", {
          key: 0,
          ref_key: "youtubeIframe",
          ref: youtubeIframe,
          src: youtubeUrl.value,
          frameborder: "0",
          allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          allowfullscreen: "",
          class: "video-iframe"
        }, null, 8 /* PROPS */, _hoisted_1))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Vimeo "),
    (__props.player === 'vimeo' && __props.videoId)
      ? (_openBlock(), _createElementBlock("iframe", {
          key: 1,
          ref_key: "vimeoIframe",
          ref: vimeoIframe,
          src: vimeoUrl.value,
          frameborder: "0",
          allow: "autoplay; fullscreen; picture-in-picture",
          allowfullscreen: "",
          class: "video-iframe"
        }, null, 8 /* PROPS */, _hoisted_2))
      : _createCommentVNode("v-if", true)
  ], 4 /* STYLE */))
}
}

})